import React, { useState } from "react";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import { useSection } from "../context/SiteContentContext";
import { Linkedin, ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import SEO from "../components/common/SEO";

const DEFAULT_PRINCIPLES = [
    { n: "01", title: "Measure first.", body: "Every product is built around a measurement — photonic, spatial, or behavioral — and designed to preserve its physics end-to-end." },
];

const ROW_1 = [
    {
        slug: "malladi",
        name: "Dr. Satyanarayana Malladi",
        role: "Vice President – R&D",
        img: "/team/malladi.jpg",
        linkedin: "https://www.linkedin.com/in/malladi-satyanarayana-37918433/",
        bio: "Dr. Satyanarayana Malladi is a distinguished space scientist and technology leader with over four decades of experience in advanced sensing systems, photonics, and aerospace engineering. He served in senior scientific and leadership roles at the Indian Space Research Organisation, including the Vikram Sarabhai Space Centre, where he led the development of laser-based sensing systems, satellite payloads, and high-precision remote sensing instrumentation for national missions. A recognized expert in optical and photonic sensing technologies, Dr. Malladi has driven the end-to-end development of complex sensor systems—from concept and design to deployment—enabling high-fidelity data acquisition for Earth observation and strategic applications. His work forms a critical foundation for next-generation sensing platforms, including the integration of sensors with AI-driven geospatial intelligence (GeoAI), digital twins, and advanced analytics.",
    },
    {
        slug: "jayant",
        name: "Jayant Athalekar",
        role: "Advisor",
        img: "/team/jayant.png",
        linkedin: "https://www.linkedin.com/in/jayant-athalekar-66a29484/",
        bio: "Jayant Athalekar is a serial entrepreneur and veteran of the oilfield equipment and heavy engineering industry, with over five decades of experience spanning energy, power systems, and industrial manufacturing. A Mechanical Engineering graduate from COEP Technological University, he has founded and scaled multiple engineering ventures, building deep expertise in product design, process engineering, and industrial commercialization. His experience spans the full lifecycle — from concept and design to manufacturing, deployment, and aftermarket support — providing a practical foundation for scaling deep-tech hardware businesses, particularly for deployment in extreme and mission-critical environments across energy, infrastructure, and industrial sectors.",
    },
    {
        slug: "ravi",
        name: "Dr. Ravi Sadasivuni",
        role: "Vice President – R&D",
        img: "/team/ravi.jpg",
        linkedin: "https://www.linkedin.com/in/ravi-sadasivuni-697851289/",
        bio: "Dr. Ravi Sadasivuni is a strategic innovation leader operating at the intersection of advanced sensing systems, GeoAI, and large-scale digital infrastructure. Currently a visiting scientist at the Indian School of Business, he brings extensive US-based experience across academia, federal agencies, and industry, with deep expertise in remote sensing, LiDAR, satellite analytics, and AI-driven spatial modeling. An AGU-nominated scientist in the United States, Dr. Sadasivuni is known for his 'sensor-to-services' approach — integrating hardware sensing, cloud data pipelines, and GeoAI models into scalable, real-world applications. He has contributed to large-scale geospatial systems in the US, including disaster response during Hurricane Katrina, for which he received a U.S. Presidential Citation Award.",
    },
];

const ROW_2 = [
    {
        slug: "nikhil",
        name: "Nikhil Chahare",
        role: "Director",
        img: "/team/nikhil.jpg",
        linkedin: "https://www.linkedin.com/in/nikhil-chahare-93774014",
        bio: "Nikhil Chahare brings over 15 years of experience as a management consultant working with Fortune 100 companies across manufacturing, oil & gas, and industrial systems, including significant exposure to US-based enterprises. A graduate of Northeastern University, he leads the strategic vision around intelligent sensing platforms, combining AI, advanced analytics, and hardware innovation. His strength lies in translating complex technologies into scalable, market-ready solutions, with a sharp focus on commercialization, product-market fit, and global expansion.",
    },
    {
        slug: "niranjan",
        name: "Niranjan Kodgule",
        role: "Director",
        img: "/team/niranjan.jpg",
        linkedin: "https://www.linkedin.com/in/niranjan-kodgule-01a209a1/",
        bio: "Niranjan Kodgule brings deep operational and manufacturing expertise with experience across US and global pharmaceutical markets. Holding a Master's degree from Rochester Institute of Technology, he specializes in building high-reliability, precision-engineered systems at scale. His focus is on establishing robust manufacturing infrastructure and quality frameworks required for next-generation sensor hardware, enabling seamless transition from prototype to scaled production.",
    },
    {
        slug: "ashwini",
        name: "Ashwini Athalekar",
        role: "Director",
        img: "/team/ashwini.jpg",
        linkedin: "https://www.linkedin.com/in/ashwiniathalekar/",
        bio: "Ashwini Athalekar brings over a decade of experience across manufacturing operations, robotics strategy, and data analytics, with professional exposure in the United States. Educated at Northeastern University, she focuses on bridging hardware and software through intelligent systems design. Her strengths lie in scaling complex programs, integrating sensor technologies with automation, and driving data-centric decision-making across industrial and research applications.",
    },
    {
        slug: "ankita",
        name: "Ankita Athalekar",
        role: "Director",
        img: "/team/ankita.jpg",
        linkedin: "https://www.linkedin.com/in/ankitaathalekar/",
        bio: "Ankita Athalekar brings a strong foundation in software systems, data infrastructure, and enterprise technology, with extensive experience in US-based financial and technology sectors. A graduate of Boston University, she leads the development of digital platforms that power connected sensor ecosystems. Her expertise in system architecture and data integration enables scalable, real-time monitoring and analytics solutions critical for next-generation sensing applications.",
    },
];

function firstSentences(text, maxChars = 180) {
    if (!text) return "";
    if (text.length <= maxChars) return text;
    const slice = text.slice(0, maxChars);
    const lastDot = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("— "));
    const cut = lastDot > 80 ? lastDot + 1 : slice.lastIndexOf(" ");
    return slice.slice(0, cut).trim() + "…";
}

function Avatar({ person, size = "md" }) {
    const classes = {
        sm: "w-24 h-24 md:w-28 md:h-28",
        md: "w-28 h-28 md:w-32 md:h-32",
        lg: "w-40 h-40 md:w-48 md:h-48",
    };
    return (
        <div
            className={`${classes[size]} rounded-full overflow-hidden relative`}
            style={{
                padding: 2,
                background:
                    "conic-gradient(from 270deg, #ff2d4f, #ff7a00, #ffd600, #00e676, #00b0ff, #5a6bff, #c56bff, #ff2d4f)",
            }}
        >
            <div className="w-full h-full rounded-full overflow-hidden bg-[#050510] border-[3px] border-[#050510]">
                <img
                    src={person.img}
                    alt={person.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                />
            </div>
        </div>
    );
}

function PersonCard({ person, onOpen, size = "md" }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="tech-card !p-6 md:!p-7 flex flex-col items-center text-center w-full h-full text-left cursor-pointer group transition-transform hover:-translate-y-1"
            data-testid={`team-${person.slug}`}
        >
            <div className="mb-4">
                <Avatar person={person} size={size} />
            </div>
            <h3 className="font-serif-display text-xl md:text-2xl leading-tight mb-1 text-center">
                {person.name}
            </h3>
            <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--accent-indigo)] mb-4">
                {person.role}
            </div>
            <p
                className="text-white/65 text-[13px] leading-relaxed"
                style={{ textAlign: "justify", textJustify: "inter-word", hyphens: "auto" }}
            >
                {firstSentences(person.bio, 200)}
            </p>
            <span className="mt-auto pt-5 inline-flex items-center gap-1.5 font-mono-tech text-[10.5px] tracking-[0.22em] uppercase text-white/60 group-hover:text-[var(--accent-indigo)] transition-colors">
                Read full bio <ArrowUpRight size={12} />
            </span>
        </button>
    );
}

function PersonDialog({ person, open, onOpenChange }) {
    if (!person) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl w-[calc(100%-2rem)] bg-[#0a0a18] border-white/10 p-0 overflow-hidden max-h-[90vh] flex flex-col"
                data-testid={`dialog-${person.slug}`}
            >
                <div className="grid grid-cols-1 md:grid-cols-12 overflow-y-auto">
                    <div className="md:col-span-4 px-6 pt-12 pb-8 md:p-10 flex flex-col items-center justify-center text-center bg-[#05050e] border-b md:border-b-0 md:border-r border-white/10">
                        <Avatar person={person} size="lg" />
                        <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-6">
                            {person.name}
                        </h3>
                        <div className="font-mono-tech text-[10.5px] tracking-[0.22em] uppercase text-[var(--accent-indigo)] mt-2">
                            {person.role}
                        </div>
                        <a
                            href={person.linkedin.startsWith("http") ? person.linkedin : `https://${person.linkedin}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 btn-secondary !py-2 !px-4"
                            data-testid={`dialog-linkedin-${person.slug}`}
                        >
                            <Linkedin size={14} /> LinkedIn
                        </a>
                    </div>
                    <div className="md:col-span-8 p-6 md:p-10">
                        <div className="overline mb-4">Biography</div>
                        <p
                            className="text-white/80 text-[14.5px] md:text-[15px] leading-[1.75]"
                            style={{ textAlign: "justify", textJustify: "inter-word", hyphens: "auto" }}
                        >
                            {person.bio}
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function About() {
    const PRINCIPLES = useSection("about_principles", DEFAULT_PRINCIPLES);
    const [selected, setSelected] = useState(null);
    return (
        <>
            <SEO
                title="About — Built Around the Photon · Skovomax"
                description="Seven engineers, scientists and operators — spanning ISRO-grade photonics, GeoAI research in the US, heavy-engineering commercialization, and modern software infrastructure."
            />
            <section className="pt-28 pb-16 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-25 pointer-events-none" />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="About / 08" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Built around <br />
                            <span className="italic text-white/70">the photon.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
                            Skovomax is an advanced sensing and systems-intelligence company. We develop
                            LiDAR systems, photonic sensors, fiber laser sources, and GeoAI analytics — and we
                            integrate them into real-world decision systems for industry, government and research.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {PRINCIPLES.map((p, i) => (
                        <Reveal delay={i * 80} key={p.n}>
                            <TechCard className="!p-10 h-full">
                                <div className="font-mono-tech text-[11px] tracking-[0.22em] text-[var(--accent-indigo)] mb-6">
                                    {p.n} / Principle
                                </div>
                                <h3 className="font-serif-display text-3xl leading-tight mb-4">{p.title || p.t}</h3>
                                <p className="text-white/60 leading-relaxed">{p.body || p.b}</p>
                            </TechCard>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-24 border-t border-white/10 bg-[#0a0a18]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <SectionLabel label="Focus" />
                        <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                            Photonics · GeoAI · Systems.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 space-y-6 text-white/65 text-lg leading-relaxed">
                        <p>
                            Our engineering practice bridges optical physics, machine learning and geospatial
                            science. We operate in partnership with agencies, utilities, and research labs
                            — developing both the measurement hardware and the analytic systems that convert
                            those measurements into decisions.
                        </p>
                        <p>
                            We are engineers first. Every deliverable is validated against physical ground
                            truth, documented for reproducibility, and designed to integrate into real
                            operational pipelines.
                        </p>
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="py-28 border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                        <div className="md:col-span-5">
                            <SectionLabel number="03" label="Leadership" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                The team <br />
                                <span className="italic text-white/70">behind the measurement.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/60 leading-relaxed text-lg">
                                Seven engineers, scientists, and operators — spanning ISRO-grade photonics,
                                GeoAI research in the US, heavy-engineering commercialization, and modern
                                software infrastructure.
                            </p>
                        </div>
                    </div>

                    {/* Row 1 — 3 senior members */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 items-stretch">
                        {ROW_1.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 90} className="h-full">
                                <PersonCard person={p} size="md" onOpen={() => setSelected(p)} />
                            </Reveal>
                        ))}
                    </div>

                    {/* Row 2 — 4 directors */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                        {ROW_2.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 90} className="h-full">
                                <PersonCard person={p} size="sm" onOpen={() => setSelected(p)} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <PersonDialog person={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
        </>
    );
}
